import { useCallback, useState } from "react";
import {
  reactQueryDelete,
  reactQueryGet,
  reactQueryPatch,
  reactQueryPost,
} from "@/react/helpers/backendRequestHelpers";
import {
  newNotificationsCountPath,
  notificationsApi,
} from "../../helpers/urlHelpers";

interface Notification {
  [key: string]: unknown; // For other notification properties
  id: number;
  post_follower_id: number | null;
  post_id: number;
  read_at: string | null;
}

export const useNotificationsApi = (perPage = 30) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [newNotificationCount, setNewNotificationCount] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const fetchNewNotificationsCount = (communityId: any) =>
    reactQueryGet(
      newNotificationsCountPath() + `?community_id=${communityId}`,
    ).then((data: any) => {
      setNewNotificationCount(data.new_notifications_count);
      if (data.new_notifications_count) {
        setIsDataLoaded(false);
      }
    });

  const fetchNotifications = (page: any, notificationGroup: any) =>
    reactQueryGet(
      `internal_api/notifications?page=${page}&per_page=${perPage}&notification_group=${notificationGroup}`,
    ).then(data => {
      if (page === 1) {
        setNotifications([...data.records]);
      } else {
        setNotifications(prev => [...prev, ...data.records]);
      }
      setIsDataLoaded(true);
      setHasNextPage(data.has_next_page);
    });

  const fetchFirstPage = async (notificationGroup: any) => {
    await fetchNotifications(1, notificationGroup);
  };

  const resetNewNotificationsCount = () => {
    if (!newNotificationCount) {
      return;
    }
    reactQueryPost(notificationsApi.resetNotificationCount())
      .then(data => {
        setNewNotificationCount(data.new_notifications_count);
      })
      .catch(error => {
        console.error(error);
      });
  };

  const markAllAsRead = () => {
    reactQueryPost(notificationsApi.markAllAsRead())
      .then(() => {
        const read_at = new Date().toString();
        const updatedNotifications = notifications.map(n => ({
          ...n,
          read_at,
        }));
        setNotifications(updatedNotifications);
      })
      .catch(error => {
        console.error(error);
      });
  };

  const markAsRead = (notification: any) => {
    if (notification.read_at) {
      return;
    }
    reactQueryPatch(notificationsApi.markAsRead({ id: notification.id }))
      .then((data: any) => {
        const updatedNotifications = notifications.map(n => {
          if (n.id === notification.id) {
            return data.record;
          }
          return n;
        });
        setNotifications(updatedNotifications);
      })
      .catch(error => {
        console.error(error);
      });
  };

  const archiveNotification = (notification: any) => {
    reactQueryPatch(`internal_api/notifications/${notification.id}/archive`)
      .then(() => {
        const updatedNotifications = notifications.filter(
          n => n.id !== notification.id,
        );
        setNotifications(updatedNotifications);
      })
      .catch(error => {
        console.error(error);
      });
  };

  const unfollowNotification = (notification: any) => {
    reactQueryDelete(
      `internal_api/posts/${notification.post_id}/post_followers/${notification.post_follower_id}`,
    )
      .then((data: any) => {
        const updatedNotifications = notifications.map(n => {
          if (n.id === notification.id && data.success) {
            n.post_follower_id = null;
            return n;
          }
          return n;
        });
        setNotifications(updatedNotifications);
      })
      .catch(error => {
        console.error(error);
      });
  };

  const followNotification = (notification: any) => {
    reactQueryPost(`internal_api/posts/${notification.post_id}/post_followers`)
      .then((data: any) => {
        const updatedNotifications = notifications.map(n => {
          if (n.id === notification.id && data.success) {
            n.post_follower_id = data.post_follower_id;
            return n;
          }
          return n;
        });
        setNotifications(updatedNotifications);
      })
      .catch(error => {
        console.error(error);
      });
  };

  const resizeNotificationWindow = useCallback(() => {
    const vheightouter = document.getElementById(
      "bottomBarNotificationWrapperOuter",
    );
    const vheightinner = document.getElementById(
      "bottomBarNotificationWrapperInner",
    );

    if (vheightouter) {
      const height = window.innerHeight - 56;
      vheightouter.style.height = height + "px";
    }
    if (vheightinner) {
      const height = window.innerHeight - 215;
      vheightinner.style.height = height + "px";
    }
  }, []);

  const onNotificationEventReceive = (eventData: any) => {
    switch (eventData.event) {
      case "resetNewNotificationCount":
        setNewNotificationCount(eventData.new_notifications_count);
        break;
      case "updateNewNotificationCount":
        setNewNotificationCount(eventData.new_notifications_count);
        break;
      case "newNotification":
        setNewNotificationCount(newNotificationCount + 1);
        break;
    }
  };

  return {
    dataLoaded: isDataLoaded,
    setDataLoaded: setIsDataLoaded,
    notifications,
    newNotificationCount,
    setNewNotificationCount,
    hasNextPage,
    setHasNextPage,
    fetchNotifications,
    fetchFirstPage,
    resetNewNotificationsCount,
    markAsRead,
    markAllAsRead,
    archiveNotification,
    fetchNewNotificationsCount,
    resizeNotificationWindow,
    onNotificationEventReceive,
    unfollowNotification,
    followNotification,
  };
};
